// src/lib/questions.ts — Load and query question data

import type { Question, CategorySlug } from '@/types';

// Import all question JSON files at build time (static, no runtime fetching)
import flutterCore from '@/data/questions/flutter-core.json';
import dartLanguage from '@/data/questions/dart-language.json';
import widgetTree from '@/data/questions/widget-tree.json';
import stateManagement from '@/data/questions/state-management.json';
import navigation from '@/data/questions/navigation.json';
import animations from '@/data/questions/animations.json';
import testing from '@/data/questions/testing.json';
import performance from '@/data/questions/performance.json';
import architecture from '@/data/questions/architecture.json';
import blocPattern from '@/data/questions/bloc-pattern.json';
import networking from '@/data/questions/networking.json';
import oopConcepts from '@/data/questions/oop-concepts.json';
import dsaDart from '@/data/questions/dsa-dart.json';
import flutterWeb from '@/data/questions/flutter-web.json';
import devopsFlavors from '@/data/questions/devops-flavors.json';

const ALL_QUESTIONS: Question[] = [
  ...(flutterCore as Question[]),
  ...(dartLanguage as Question[]),
  ...(widgetTree as Question[]),
  ...(stateManagement as Question[]),
  ...(navigation as Question[]),
  ...(animations as Question[]),
  ...(testing as Question[]),
  ...(performance as Question[]),
  ...(architecture as Question[]),
  ...(blocPattern as Question[]),
  ...(networking as Question[]),
  ...(oopConcepts as Question[]),
  ...(dsaDart as Question[]),
  ...(flutterWeb as Question[]),
  ...(devopsFlavors as Question[]),
];

export function getAllQuestions(): Question[] {
  return ALL_QUESTIONS;
}

export function getQuestionBySlug(slug: string): Question | undefined {
  return ALL_QUESTIONS.find((q) => q.slug === slug);
}

export function getQuestionById(id: string): Question | undefined {
  return ALL_QUESTIONS.find((q) => q.id === id);
}

export function getQuestionsByCategory(category: CategorySlug): Question[] {
  return ALL_QUESTIONS.filter((q) => q.category === category);
}

export function getRelatedQuestions(question: Question, limit = 4): Question[] {
  return question.relatedQuestions
    .map((id) => ALL_QUESTIONS.find((q) => q.id === id))
    .filter((q): q is Question => q !== undefined)
    .slice(0, limit);
}

export function getAllSlugs(): string[] {
  return ALL_QUESTIONS.map((q) => q.slug);
}

export function getAllTags(): string[] {
  const tagSet = new Set<string>();
  ALL_QUESTIONS.forEach((q) => q.tags.forEach((t) => tagSet.add(t)));
  return Array.from(tagSet).sort();
}

export function getTopTags(limit = 30): string[] {
  const tagCount = new Map<string, number>();
  ALL_QUESTIONS.forEach((q) => {
    q.tags.forEach((t) => tagCount.set(t, (tagCount.get(t) ?? 0) + 1));
  });
  return Array.from(tagCount.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([tag]) => tag);
}

export function getAdjacentQuestions(
  question: Question
): { prev: Question | null; next: Question | null } {
  const categoryQuestions = getQuestionsByCategory(question.category);
  const idx = categoryQuestions.findIndex((q) => q.id === question.id);
  return {
    prev: idx > 0 ? (categoryQuestions[idx - 1] ?? null) : null,
    next: idx < categoryQuestions.length - 1 ? (categoryQuestions[idx + 1] ?? null) : null,
  };
}

export function getTotalQuestionCount(): number {
  return ALL_QUESTIONS.length;
}
